import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import { animeList, categories, siteConfig } from '@/mocks/anime';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'recent');
  const [sortBy, setSortBy] = useState<'rating' | 'date' | 'title'>('rating');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);

  const filteredAnime = useMemo(() => {
    let list = [...animeList];
    if (activeCategory === 'recent') {
      list = list.sort((a, b) => b.rating - a.rating);
    } else if (activeCategory === 'finished') {
      list = list.filter((a) => a.updateInfo.includes('已完结') || a.updateInfo.includes('完整版'));
    } else {
      list = list.filter((a) => a.category === activeCategory);
    }

    if (sortBy === 'rating') {
      list = list.sort((a, b) => b.rating - a.rating);
    } else if (sortBy === 'date') {
      list = list.sort((a, b) => new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime());
    } else if (sortBy === 'title') {
      list = list.sort((a, b) => a.title.localeCompare(b.title, 'zh'));
    }
    return list;
  }, [activeCategory, sortBy]);

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: siteConfig.url,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: currentCategory?.name || '分类',
        item: `${siteConfig.url}/category/${activeCategory}`,
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <Navbar />
      <main className="min-h-screen pt-16">
        <section className="bg-background-100/50 border-b border-background-200/50">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-10">
            <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-4" aria-label="面包屑导航">
              <Link to="/" className="hover:text-accent-400 transition-colors whitespace-nowrap cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-200">{currentCategory?.name || '分类'}</span>
            </nav>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-50 mb-2">
              {currentCategory?.name || '内容分类'}
            </h1>
            <p className="text-sm text-foreground-400">{currentCategory?.description}</p>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 md:px-6 py-4 border-b border-background-200/50 overflow-x-auto">
          <div className="flex items-center gap-1 flex-wrap min-w-max py-2">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                onClick={() => setActiveCategory(cat.slug)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  activeCategory === cat.slug
                    ? 'bg-accent-500 text-background-50 dark:text-foreground-950'
                    : 'text-foreground-400 hover:text-foreground-100 hover:bg-background-200/50'
                }`}
              >
                <i className={`${cat.icon} mr-1.5`}></i>
                {cat.name}
              </Link>
            ))}
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 md:px-6 py-4 flex items-center justify-between">
          <p className="text-sm text-foreground-500">
            共 <strong className="text-foreground-200">{filteredAnime.length}</strong> 部作品
          </p>
          <div className="flex items-center gap-2">
            <span className="text-xs text-foreground-500">排序：</span>
            {[
              { key: 'rating' as const, label: '评分' },
              { key: 'date' as const, label: '时间' },
              { key: 'title' as const, label: '名称' },
            ].map((opt) => (
              <button
                key={opt.key}
                onClick={() => setSortBy(opt.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                  sortBy === opt.key
                    ? 'bg-primary-500/20 text-primary-400'
                    : 'text-foreground-500 hover:text-foreground-200'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 md:px-6 pb-14">
          {filteredAnime.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
              {filteredAnime.map((anime) => (
                <Link
                  key={anime.id}
                  to={`/article/${anime.id}`}
                  className="group block rounded-lg overflow-hidden bg-background-100 hover:bg-background-200/70 transition-all cursor-pointer"
                >
                  <div className="relative aspect-[3/4] overflow-hidden">
                    <img
                      src={anime.coverImage}
                      alt={anime.title}
                      title={`${anime.title} - 樱花动漫网${currentCategory?.name || ''}在线观看`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                    />
                    <div className="absolute top-2 right-2 px-2 py-1 bg-background-950/80 backdrop-blur-sm rounded-md">
                      <span className="text-xs font-medium text-secondary-400">{anime.rating}</span>
                    </div>
                  </div>
                  <div className="p-3 md:p-4">
                    <h3 className="text-sm md:text-base font-medium text-foreground-100 group-hover:text-accent-400 transition-colors line-clamp-1">
                      {anime.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-1">{anime.updateInfo}</p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {anime.tags.slice(0, 2).map((tag) => (
                        <span key={tag} className="px-2 py-0.5 text-xs rounded-md bg-accent-100/10 text-accent-400 border border-accent-200/20">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <i className="ri-inbox-line text-4xl text-foreground-600 mb-4 block"></i>
              <p className="text-foreground-500">该分类暂无内容，请查看其他分类</p>
            </div>
          )}
        </section>
      </main>
      <Footer />
    </>
  );
}